import React, { useState, useMemo } from 'react';
import { FiPower, FiRadio, FiShield, FiCpu, FiMapPin } from 'react-icons/fi';
import { useNetworkStats } from './hooks/useNetworkStats';
import { getConnectionType } from './utils/networkUtils';
import SignalMonitor from './components/SignalMonitor';
import DNSIntelligence from './components/DNSIntelligence';
import RecommendationEngine from './components/RecommendationEngine';
import NetworkProviderIntelligence from './components/NetworkProviderIntelligence';
import ConnectionTypeBadge from './components/ConnectionTypeBadge';
import PerformanceComparison from './components/PerformanceComparison';
import CongestionIntelligence from './components/CongestionIntelligence';
import { motion, AnimatePresence } from 'framer-motion';

function App() {
  const [isRunning, setIsRunning] = useState(false);
  const [aiMode, setAiMode] = useState(false);
  const [geoMode, setGeoMode] = useState(false);
  const { stats } = useNetworkStats(isRunning);
  const connectionInfo = useMemo(() => getConnectionType(), [isRunning]);

  return (
    <div className="min-h-screen bg-background text-text p-4 pb-12 max-w-md mx-auto selection:bg-primary/30">
      <header className="flex justify-between items-center mb-6 pt-2">
        <div>
          <h1 className="text-lg font-black text-white tracking-tighter uppercase">
            NetTune <span className="text-primary">Tester</span>
          </h1>
          <p className="text-[10px] text-muted font-bold uppercase tracking-widest leading-tight">
            Network Intelligence Core
          </p>
        </div>
        <button 
          onClick={() => setIsRunning(!isRunning)}
          className={`relative flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-500 ${isRunning ? 'border-primary bg-primary/10 text-primary shadow-[0_0_20px_rgba(16,185,129,0.3)]' : 'border-white/10 bg-white/5 text-muted hover:border-white/20'}`}
        >
          <FiPower size={20} className={isRunning ? "drop-shadow-[0_0_8px_rgba(16,185,129,1)]" : ""} />
        </button>
      </header>

      {/* Prominent Connection Indicator */}
      <div className="mb-8">
        <ConnectionTypeBadge connectionInfo={connectionInfo} />
      </div>

      <main className="space-y-8">
        {!isRunning ? (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-panel rounded-2xl p-10 border-dashed border-white/10 text-center space-y-4"
          >
            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto border border-white/5 shadow-inner">
              <FiShield className="text-muted" size={24} />
            </div>
            <div className="space-y-1">
              <h2 className="text-lg font-bold text-white uppercase tracking-tight">System Idle</h2>
              <p className="text-xs text-muted leading-relaxed font-medium">
                Connect to the intelligence core to begin AI & Geo-Location SEO optimization.
              </p>
            </div>
            <button 
              onClick={() => setIsRunning(true)}
              className="w-full py-3 bg-primary text-background font-black rounded-xl text-xs uppercase tracking-widest hover:brightness-110 active:scale-95 transition-all shadow-lg shadow-primary/20"
            >
              Initialize Engine
            </button>
          </motion.div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div 
              key="active-content"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-8"
            >
              <SignalMonitor stats={stats} connectionInfo={connectionInfo} />
              <NetworkProviderIntelligence provider={stats.provider} isRunning={isRunning} />

              <div className="grid grid-cols-2 gap-3">
                <button 
                  onClick={() => setAiMode(!aiMode)}
                  className={`group p-3 rounded-xl border text-left transition-all duration-300 ${aiMode ? 'bg-primary/10 border-primary/50 shadow-[0_0_15px_rgba(16,185,129,0.1)]' : 'bg-surfaceHighlight/30 border-white/5'}`}
                >
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[10px] text-muted uppercase font-black tracking-tighter">AI SEO</span>
                    <FiCpu className={aiMode ? "text-primary" : "text-muted"} size={12} />
                  </div>
                  <div className={`text-xs font-bold ${aiMode ? 'text-primary' : 'text-text'}`}>
                    {aiMode ? 'OPTIMIZING' : 'DISABLED'}
                  </div>
                </button>
                <button 
                  onClick={() => setGeoMode(!geoMode)}
                  className={`group p-3 rounded-xl border text-left transition-all duration-300 ${geoMode ? 'bg-primary/10 border-primary/50 shadow-[0_0_15px_rgba(16,185,129,0.1)]' : 'bg-surfaceHighlight/30 border-white/5'}`}
                >
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[10px] text-muted uppercase font-black tracking-tighter">GEO SEO</span>
                    <FiMapPin className={geoMode ? "text-primary" : "text-muted"} size={12} />
                  </div>
                  <div className={`text-xs font-bold ${geoMode ? 'text-primary' : 'text-text'}`}>
                    {geoMode ? 'TRACKING' : 'DISABLED'}
                  </div>
                </button>
              </div>

              <RecommendationEngine stats={stats} aiMode={aiMode} geoMode={geoMode} />
            </motion.div>
          </AnimatePresence>
        )}
        
        <DNSIntelligence stats={stats} isRunning={isRunning} />

        {isRunning && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="space-y-8"
          >
            <PerformanceComparison stats={stats} connectionInfo={connectionInfo} />
            <CongestionIntelligence stats={stats} isRunning={isRunning} />
          </motion.div>
        )}
      </main>

      <footer className="mt-12 text-center space-y-4">
        <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/5 border border-white/5 text-[9px] font-bold text-muted uppercase tracking-widest shadow-sm">
          <span className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse shadow-[0_0_5px_rgba(16,185,129,1)]" />
          Core v2.8.0 • Optimized
        </div>
      </footer>
    </div>
  );
}

export default App;